import { truckListQuerySchema, type Page, type TruckListQuery } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { toListParams } from '../../../../../shared/policies/list-params';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { listTrucks, type TruckListItem } from '../trucks.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type TrucksState =
  { status: 'loading' } | { status: 'loaded'; page: Page<TruckListItem>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: TruckListQuery) => toListParams(query).toString();

function toState(page: Page<TruckListItem>, query: TruckListQuery): TrucksState {
  return { status: 'loaded', page, queryKey: keyOf(query) };
}

function toErrorState(err: unknown): TrucksState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of trucks the URL's list query asks for (`useListQuery`), re-fetched whenever that query changes. */
export function useTruckList() {
  const list = useListQuery(truckListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<TrucksState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    listTrucks(query)
      .then((page) => {
        if (!cancelled) setState(toState(page, query));
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  /** Re-fetches the current page after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState(toState(await listTrucks(query), query));
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, list, isRefreshing, reload };
}
